import { Component, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { GlobalService } from '../../services/global.service';
import { ListActionButtonsComponent } from '../../components/list-action-buttons/list-action-buttons.component';
import { ConfirmationPopupComponent } from '../../components/confirmation-popup/confirmation-popup.component';
import { ModalComponent } from '../../components/modal/modal.component';
import { ActivatedRoute, Router } from '@angular/router';
import { AgGridListComponent } from "../../components/ag-grid-list/ag-grid-list.component";
import { ButtonComponent } from "../../components/button/button.component";
import { HeaderComponent } from "../../components/header/header.component";
import { TextInputComponent } from "../../components/text-input/text-input.component";
import { PluralPipe } from '../../pipes/plural.pipe';
import { DistrictService } from '../../services/district.service';
import { SelectInputComponent } from '../../components/select-input/select-input.component';
import { CommonModule, JsonPipe } from '@angular/common';
import { DropdownService } from '../../services/dropdown.service';
import { BulkUploadComponent } from '../../components/bulk-upload/bulk-upload.component';
import { SearchInputComponent } from '../../components/search-input/search-input.component';
import { FilterByKeyAndValuePipe } from '../../pipes/filter-by-key-and-value.pipe';
import { ValidationService } from '../../services/validation.service';
import { TableDropdownComponent } from '../../components/table-dropdown/table-dropdown.component';
import { ScriptService } from '../../services/script.service';
import { UserService } from '../../services/user.service';
import { EvaluationService } from '../../services/evaluation.service';

@Component({
  selector: 'app-evaluator-mapping-list-page',
  imports: [HeaderComponent, ButtonComponent, CommonModule, ModalComponent, ReactiveFormsModule, FormsModule, PluralPipe, SelectInputComponent, AgGridListComponent, SearchInputComponent, FilterByKeyAndValuePipe],
  templateUrl: './evaluator-mapping-list-page.component.html',
  styleUrl: './evaluator-mapping-list-page.component.css'
})
export class EvaluatorMappingListPageComponent {
  @ViewChild('form_modal') form_modal: ModalComponent | undefined;
  @ViewChild('list') list!: AgGridListComponent;

  page_name = 'Assign Evaluator';
  form: FormGroup;
  params: any = {};
  selected_ids: any = {}
  items: any[] = [];
  isSubmitting = false;
  subject_id: number | null = null;
  filtered_evaluator_list: any[] = [];

  filteredUniversities: any[] = [];
  filteredAcademicYears: any[] = [];
  filteredExams: any[] = [];
  filteredAssessments: any[] = [];
  lastUniversityId: any = null;
  columns: any = [];

  constructor(public gs: GlobalService, public route: Router, public fb: FormBuilder, public ds: ScriptService, public es: EvaluationService, public ar: ActivatedRoute, public dds: DropdownService, public vs: ValidationService) {
    this.form = this.fb.group({
      evaluator_id: ['', [Validators.required]],
    });
  }

  async ngOnInit() {
    await this.dds?.get_dropdown_list(['university', 'academic-year', 'exam', 'manual_assessment', 'evaluator']);

    this.initializeColumns();
    this.rebuild_filter_lists();

    this.ar.queryParams.subscribe(async params => {
      this.params = { ...params }
      this.rebuild_filter_lists();
      if (this.params.assessment_id) {
        await this.get_list(this.params)
      }
    })
  }

  rebuild_filter_lists() {
    const manual = this.dds.dropdown_list?.manual_assessment_list || [];

    // Universities that have Manual assessments
    const uni_ids = [...new Set(manual.map((a: any) => a.university_id).filter(Boolean))];
    this.filteredUniversities = (this.dds.dropdown_list?.university_list || []).filter((u: any) => uni_ids.includes(u.id));

    // Academic years for selected university
    if (this.params.university_id) {
      const ay_ids = [...new Set(manual.filter((a: any) => a.university_id == this.params.university_id).map((a: any) => a.academic_year_id).filter(Boolean))];
      this.filteredAcademicYears = (this.dds.dropdown_list?.academic_list || []).filter((ay: any) => ay_ids.includes(ay.id));
    } else {
      this.filteredAcademicYears = [];
    }

    // Exams for selected university + academic year
    if (this.params.university_id && this.params.academic_year_id) {
      const exam_ids = [...new Set(manual.filter((a: any) => a.university_id == this.params.university_id && a.academic_year_id == this.params.academic_year_id).map((a: any) => a.exam_id).filter(Boolean))];
      this.filteredExams = (this.dds.dropdown_list?.exam_list || []).filter((e: any) => exam_ids.includes(e.id));
    } else {
      this.filteredExams = [];
    }

    // Assessments for selected university + academic year + exam
    if (this.params.university_id && this.params.academic_year_id && this.params.exam_id) {
      this.filteredAssessments = manual.filter((a: any) =>
        a.university_id == this.params.university_id &&
        a.academic_year_id == this.params.academic_year_id &&
        a.exam_id == this.params.exam_id
      );
    } else {
      this.filteredAssessments = [];
    }
  }

  initializeColumns() {
    this.columns = [
      { field: 'script_id', headerName: 'Script Id', filter: true, flex: 1, },
      { field: 'barcode', headerName: 'Barcode', filter: true, flex: 1, },
      {
        field: 'assigned_evaluator.HeadEvaluator.name', headerName: 'Head Evaluator', filter: true, flex: 1,
        cellRenderer: (params: any) => {
          const name = params.data?.assigned_evaluator?.HeadEvaluator?.name;
          return name
            ? `<div class="flex items-center h-full">${name}</div>`
            : `<div class="flex items-center h-full"><span style="color:#b0b0b0;font-style:italic;">Select Evaluator</span></div>`;
        }
      },
      {
        field: 'status', headerName: 'Status', filter: true, flex: 1,
        cellRenderer: (params: any) => {
          const value = params.value || 'Unassigned';
          const isAssigned = value === 'Assigned';
          return `<div class="flex items-center h-full"><span class="inline-block px-5 py-1.5 rounded-full text-sm font-semibold ${isAssigned ? 'bg-green-100 text-[#1a7a4a]' : 'bg-[#F7F7F7] text-gray-500'}">${value}</span></div>`;
        }
      },
      {
        field: 'assigned_evaluator_id',
        headerName: `Evaluator Name`,
        filter: true,
        flex: 1,
        cellRenderer: TableDropdownComponent,
        cellRendererParams: {
          disabled: !this.gs.user_role_data?.assignments_assign_evaluation_assign,
          options: this.filtered_evaluator_list.length > 0 ? this.filtered_evaluator_list : this.dds.dropdown_list?.evaluator_list,
          label: ``,
          placeholder: `Select Evaluator`,
          show_email: true,
          onChange: this.onEvaluatorChange.bind(this)
        }
      },
      // { headerName: "Actions", sortable: false, cellRenderer: ListActionButtonsComponent, headerClass: 'custom-header-center', cellRendererParams: { actions: this.actions.bind(this) }, editable: false, pinned: 'right', width: 210, minWidth: 210, maxWidth: 210 }
    ];
  }

  /* Dropdown Dependency Logic */
  dropdownRegistry: { [depended: number]: any } = {};

  registerDropdown(event: { depended: number; ref: any }) {
    this.dropdownRegistry[event.depended] = event.ref;
  }

  async onDropdownChange(event: { value: any, depended: number }) {
    console.log("Dropdown changed:", event);

    const queryParams: any = { ...this.params };

    if (event.depended == 1) {
      if (event.value) {
        queryParams.university_id = event.value.value;
      } else {
        delete queryParams.university_id;
      }
      delete queryParams.exam_id;
      delete queryParams.assessment_id;
      this.items = [];
    }
    if (event.depended == 2) {
      if (event.value) {
        queryParams.academic_year_id = event.value.value;
      } else {
        delete queryParams.academic_year_id;
      }
      delete queryParams.exam_id;
      delete queryParams.assessment_id;
      this.items = [];
    }
    if (event.depended == 3) {
      if (event.value) {
        queryParams.exam_id = event.value.value;
      } else {
        delete queryParams.exam_id;
      }
      delete queryParams.assessment_id;
      this.items = [];
    }
    if (event.depended == 4) {
      if (event.value) {
        queryParams.assessment_id = event.value.value;
      } else {
        delete queryParams.assessment_id;
        this.items = [];
      }
    }

    this.params = queryParams;
    this.rebuild_filter_lists();

    // Update Query Params
    this.route.navigate([], {
      relativeTo: this.ar,
      queryParams: queryParams,
      // queryParamsHandling: 'merge', // We are constructing the full object, so strictly replacing/merging manually is safer to avoid stale keys if we deleted them
    });

    // Note: The list fetch is triggered by the queryParams subscription in ngOnInit, 
    // BUT we might want to manually clear items if assessment_id is removed ASAP.
    // The subscription will handle the "if assessment_id then fetch" logic.

    const current = event.depended;
    for (const key of Object.keys(this.dropdownRegistry).map(Number)) {
      if (key > current) {
        this.dropdownRegistry[key]?.reset();
      }
    }
  }

  async onEvaluatorChange(new_value: any, row_data: any) {
    console.log('Evaluator changed:', new_value, 'for row:', row_data);
    try {
      if (row_data.id) {
        await this.es.submit({ assign_evaluator_id: new_value, script_ids: [row_data.id] });
        await this.get_list(this.params); // Refresh the list to show updated evaluator
        const msg = new_value ? 'Evaluator Assigned Successfully to Selected Scripts.' : 'Evaluator Unassigned Successfully from Selected Script.';
        this.gs.toastr_shows_function(msg, 'Success', 'success');
      }
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message || 'Error updating evaluator', 'Error', 'error');
    }
  }

  async get_list(filter?: any) {
    try {
      if (this.params.assessment_id) {
        filter = { assessment_id: this.params.assessment_id, assessment_type: 'Manual' }
        const res = await this.ds.get_list(filter);
        const page = filter?.page || 1;
        const pagesize = filter?.page_size || 10;
        const startindex = (page - 1) * pagesize;
        res.data = res.data.map((item: any, index: number) => ({
          ...item,
          SrNo: startindex + index + 1,
          status: item.assigned_evaluator_id ? 'Assigned' : 'Unassigned'
        }));
        this.items = res;

        // Extract subject_id from the first item's Assessment.Subject.id
        if (res.data && res.data.length > 0 && res.data[0]?.Assessment?.Subject?.id) {
          this.subject_id = res.data[0].Assessment.Subject.id;
          await this.load_evaluators_by_subject();
        }
      }
    } catch (error: any) {
      console.error("Error fetching Booking list:", error);
    }
  }

  async load_evaluators_by_subject() {
    if (this.subject_id) {
      this.filtered_evaluator_list = await this.dds.get_evaluators_by_subject(this.subject_id);
      // Update column definitions with new evaluator list
      this.initializeColumns();
    }
  }

  get_filtered_universities() {
    if (!this.dds.dropdown_list?.manual_assessment_list || !this.dds.dropdown_list?.university_list) return this.dds.dropdown_list?.university_list || [];
    const uni_ids = this.dds.dropdown_list.manual_assessment_list
      .map((a: any) => a.university_id)
      .filter((id: any, i: number, self: any[]) => id && self.indexOf(id) === i);
    return this.dds.dropdown_list.university_list.filter((u: any) => uni_ids.includes(u.id));
  }

  get_filtered_exams(university_id: any, academic_year_id: any) {
    if (!university_id || !academic_year_id || !this.dds.dropdown_list?.manual_assessment_list) return [];
    const exam_ids = this.dds.dropdown_list.manual_assessment_list
      .filter((a: any) => a.university_id == university_id && a.academic_year_id == academic_year_id)
      .map((a: any) => a.exam_id)
      .filter((id: any, i: number, self: any[]) => id && self.indexOf(id) === i);
    return this.dds.dropdown_list?.exam_list?.filter((e: any) => exam_ids.includes(e.id)) || [];
  }

  get_filtered_academic_years(university_id: any) {
    if (!university_id || !this.dds.dropdown_list?.manual_assessment_list) {
      this.filteredAcademicYears = [];
      this.lastUniversityId = null;
      return this.filteredAcademicYears;
    }

    if (university_id === this.lastUniversityId && this.filteredAcademicYears.length > 0) {
      return this.filteredAcademicYears;
    }

    const assessment_academic_year_ids = this.dds.dropdown_list.manual_assessment_list
      .filter((assessment: any) => assessment.university_id == university_id)
      .map((assessment: any) => assessment.academic_year_id)
      .filter((id: any, index: number, self: any[]) => self.indexOf(id) === index);

    this.filteredAcademicYears = this.dds.dropdown_list?.academic_list?.filter((academic: any) =>
      assessment_academic_year_ids.includes(academic.id)
    ) || [];

    this.lastUniversityId = university_id;
    return this.filteredAcademicYears;
  }

  async open_form_modal(item?: any) {
    this.selected_ids = item || {};
    this.form.reset();
    if (this.selected_ids && this.selected_ids.id) {
      let response = await this.ds.get(item.id);
      this.selected_ids = this.gs.addHierarchyIds({ ...response.data });
      this.form.patchValue({ ...this.selected_ids, });
    } else {
      this.selected_ids = {};
      this.form.reset();
    }
    this.form_modal?.open();
  }
  close_form_modal(): void {
    this.form_modal?.close();
  }

  async submit() {
    try {
      if (this.isSubmitting) {
        return;
      }
      if (this.form.invalid) {
        this.gs.toastr_shows_function('Please fill all required fields correctly.', 'Error', 'error');
        this.form.markAllAsTouched();
        return;
      }
      this.isSubmitting = true;
      let form_value = this.form.value;
      delete form_value.country_id
      form_value.is_active = true;
      if (this.selected_ids && this.selected_ids.id) {
        let update_response = await this.ds.update(this.selected_ids.id, form_value);
        this.gs.toastr_shows_function('Changes updated successfully', 'Success', 'success');
      } else {
        await this.es.submit({ assign_evaluator_id: this.form.value.evaluator_id, script_ids: this.list.selected_ids });
        // let create_response = await this.ds.add(form_value);
        // this.gs.toastr_shows_function(create_response?.message, 'Success', 'success');
      }
      await this.get_list(this.params);
      this.close_form_modal();
    } catch (error: any) {
      this.gs.toastr_shows_function(error.error.message, 'Error', 'error');
    } finally {
      this.isSubmitting = false;
    }
  }
}
